/**
 * Serialize a calendar date picked in a Datepicker as an instant that renders
 * back as the same calendar day for every viewer.
 *
 * Datepickers hand back a `Date` at LOCAL midnight. Sending that through
 * `JSON.stringify`/`toISOString()` converts it to UTC, which lands on the
 * previous calendar day for every timezone east of UTC (CP-10907). Stamping
 * the picked calendar parts at UTC NOON instead keeps both kinds of read
 * paths on the picked day:
 *   - server-side UTC date renders (TO_CHAR/::date casts, CSV exports) —
 *     correct in every timezone
 *   - viewer-local date-only renders, for every viewer from UTC-11 to UTC+11
 * UTC midnight would satisfy only the first; a local-midnight instant only
 * the picker's own timezone. Same approach as the cognota-frontend Complete
 * modal (CGP-261).
 *
 * Input contract:
 *   - `Date`: interpreted via its LOCAL calendar parts — i.e. the day the
 *     user saw in the picker.
 *   - string starting with `YYYY-MM-DD` (ISO date or full ISO instant): the
 *     literal date part is stamped, in every timezone. This avoids the JS
 *     hazard where `new Date('2026-08-20')` parses as UTC midnight but local
 *     getters read the previous day west of UTC. It also makes re-stamping
 *     an already-stamped value a no-op regardless of viewer timezone.
 *   - other strings (e.g. `'8/20/2026'`): parsed by `new Date()`, which
 *     treats them as local midnight, then read via local parts.
 *   - `undefined` / `null` / `''` pass through untouched so callers can
 *     stamp optional fields in place.
 *   - invalid dates and out-of-range ISO parts (`'not a date'`,
 *     `new Date('x')`, `'2026-13-45'`) return the input as-is rather than
 *     throwing or silently rolling over — the exported util sits on render
 *     paths where a throw would take down the form.
 */
const ISO_DATE_PREFIX = /^(\d{4})-(\d{2})-(\d{2})/;

const stampUtcNoon = (
  year: number,
  monthIndex: number,
  day: number
): string => {
  const stamped = new Date(Date.UTC(year, monthIndex, day, 12));
  // Guard against silent rollover: Date.UTC(2026, 12, 45) constructs a valid
  // Date one year and change later. Verify the constructed parts match the
  // requested calendar day and bail if they don't — the caller sees the
  // untouched input rather than a wrong day.
  if (
    stamped.getUTCFullYear() !== year ||
    stamped.getUTCMonth() !== monthIndex ||
    stamped.getUTCDate() !== day
  ) {
    return '';
  }
  return stamped.toISOString();
};

export function toUtcNoonIso(date: Date | string): string;
export function toUtcNoonIso(date: null): null;
export function toUtcNoonIso(date: undefined): undefined;
export function toUtcNoonIso(
  date: Date | string | null | undefined
): string | null | undefined;
export function toUtcNoonIso(
  date: Date | string | null | undefined
): string | null | undefined {
  if (date === null || date === undefined || date === '') {
    return date;
  }
  if (typeof date === 'string') {
    const isoParts = ISO_DATE_PREFIX.exec(date);
    if (isoParts) {
      const [, year, month, day] = isoParts;
      const stamped = stampUtcNoon(
        Number(year),
        Number(month) - 1,
        Number(day)
      );
      return stamped || date;
    }
  }
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) {
    return typeof date === 'string' ? date : '';
  }
  return stampUtcNoon(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * Inverse of `toUtcNoonIso`: a local-midnight `Date` for the UTC calendar
 * day encoded in a stamped ISO instant.
 *
 * Use at every `Datepicker` prop boundary that feeds a stamped value back
 * in (`startDate={fromUtcNoonIso(x)}`). The alternative — `new Date(x)` —
 * re-stamps `+1` day on every render at UTC+12..+14 because:
 *   - The design-system `Datepicker` syncs the `startDate`/`endDate` props
 *     into internal state on every render, and the prop identity is fresh
 *     each time.
 *   - The underlying `@datepicker-react/hooks` re-emits the PROP-derived
 *     start date when the range completes (`endDate` click 2), not the
 *     freshly-clicked one.
 *   - `toUtcNoonIso` on a fed-back `Date` reads local getters, which at
 *     UTC+12..+14 report the day after the stored UTC day — so the round
 *     trip drifts by 1 per interaction.
 *
 * Returning a **local-midnight Date of the UTC calendar day** makes the
 * round trip a fixed point in every timezone: local getters read back the
 * same day the stamp encoded.
 */
export function fromUtcNoonIso(iso: string): Date | undefined;
export function fromUtcNoonIso(iso: null): null;
export function fromUtcNoonIso(iso: undefined): undefined;
export function fromUtcNoonIso(
  iso: string | null | undefined
): Date | null | undefined;
export function fromUtcNoonIso(
  iso: string | null | undefined
): Date | null | undefined {
  if (iso === null || iso === undefined || iso === '') {
    // Empty string collapses to `undefined` — pickers treat `undefined` and
    // `''` interchangeably for "no date", and consumers get one shape.
    return iso === null ? null : undefined;
  }
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) {
    return undefined;
  }
  // Local midnight of the encoded UTC calendar day, via the (y, m, d)
  // constructor so the ambient timezone's rules — including DST transitions
  // — resolve the instant. Offset math (Date.UTC(y,m,d) plus the offset
  // sampled at THAT instant) lands one local day early whenever a DST
  // transition falls between UTC midnight and local midnight of the target
  // day (Auckland 2026-09-27, Sydney 2026-10-04, Santiago 2026-04-05), and
  // no offset correction survives a spring-forward AT local midnight
  // (Santiago 2026-09-06, where local midnight doesn't exist) — only the
  // constructor resolves that correctly.
  // Testability: the constructor reads the process's real TZ, which the
  // getter-spy strategy can't emulate — cross-timezone coverage for this
  // function lives in the execFileSync real-TZ block in
  // calendarDates.test.ts, which includes those DST-transition days.
  return new Date(
    parsed.getUTCFullYear(),
    parsed.getUTCMonth(),
    parsed.getUTCDate()
  );
}
